/**
 * <TutorChoiceStep> — une étape à choix de « Compléter mon profil » (organism).
 *
 * Les questions `select.single` et `select.multiple` de l'ancien parcours (`FormStepper`) : une
 * phrase d'aide, puis les réponses, sous la forme du design system qui convient à ce qu'on choisit
 * (Wajih, 03/11 : l'état choisi doit sauter aux yeux, et l'espace se tenir) :
 *  - `presentation="cards"` — peu de réponses, qui se lisent comme des phrases (« Oui » / « Non »,
 *    « Scientifiques » / « Littéraires ») : des cartes d'option côte à côte (`<OptionCard>`), le
 *    bouton radio pour une seule réponse, la coche pour plusieurs. Elles prennent la largeur de la
 *    colonne au lieu de deux pilules perdues à gauche ;
 *  - `presentation="chips"` (défaut) — beaucoup de réponses courtes (les classes, rangées sous
 *    Primaire, Collège, Lycée ; les niveaux d'une langue) : les puces du DS (`<ChoiceChip>`), la
 *    coche et l'aplat de l'univers quand elles sont choisies, la surface d'encart sans coche quand elles
 *    ne le sont pas — plus deux boutons qui se ressemblent ;
 *  - `subjects` — des matières : la puce de matière (`<SubjectBadge>`), son picto et sa couleur.
 *
 * Contrôlée : la sélection arrive en props, chaque réponse touchée remonte son id.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { OptionCard } from "../../molecules/OptionCard"
import { type UISubject } from "../../../ports/primary/ui/types"
import { type UISelectOption } from "../../../ports/primary/ui/types/studentAccount"
import { type UISelectOptionSection } from "../../../ports/primary/ui/types/tutorCompte"

export type TutorChoiceStepProps = {
    /** La phrase sous la question (« Tu retrouveras tes élèves chaque semaine aux mêmes horaires »). */
    description?: string
    /** Les réponses, à plat… */
    options?: UISelectOption[]
    /** …ou par section… */
    sections?: UISelectOptionSection[]
    /** …ou des matières, avec leur picto. */
    subjects?: UISubject[]
    /** `chips` (défaut) : des puces ; `cards` : des cartes d'option côte à côte. Pour `options` seulement. */
    presentation?: "chips" | "cards"
    /** Une seule réponse (« Oui » / « Non ») : le bouton radio sur les cartes. Défaut `true`. */
    multiple?: boolean
    selectedIds: string[]
    onToggle: (id: string) => void
    /** L'erreur de l'étape (« Champ obligatoire »), sous les réponses. */
    error?: string | null
    disabled?: boolean
    testID?: string
}

/** Sous cette largeur de colonne, les cartes d'option s'empilent au lieu de se faire face. */
const CARDS_SIDE_BY_SIDE_FROM = 420

export function TutorChoiceStep(props: TutorChoiceStepProps) {
    const {
        description,
        options,
        sections,
        subjects,
        presentation = "chips",
        multiple = true,
        selectedIds,
        onToggle,
        error,
        disabled = false,
        testID,
    } = props
    const { onLayout, atLeast: sideBySide } = useWidthAtLeast(CARDS_SIDE_BY_SIDE_FROM)

    const chips = (items: UISelectOption[]) => (
        <Row gap="2" style={styles.wrap}>
            {items.map((item) => (
                <ChoiceChip
                    key={item.id}
                    label={item.label}
                    selected={selectedIds.includes(item.id)}
                    disabled={disabled}
                    onPress={() => onToggle(item.id)}
                />
            ))}
        </Row>
    )

    const cards = (items: UISelectOption[]) => (
        <View style={styles.cards(sideBySide)} onLayout={onLayout}>
            {items.map((item) => (
                <View key={item.id} style={sideBySide ? styles.sideCard : undefined}>
                    <OptionCard
                        label={item.label}
                        control={multiple ? "check" : "radio"}
                        selected={selectedIds.includes(item.id)}
                        disabled={disabled}
                        onPress={() => onToggle(item.id)}
                    />
                </View>
            ))}
        </View>
    )

    return (
        <Stack gap="4" testID={testID}>
            {description != null && description !== "" && <Text color="text-muted">{description}</Text>}
            {options != null && (presentation === "cards" ? cards(options) : chips(options))}
            {sections?.map((section) => (
                <Stack key={section.id} gap="2">
                    <SectionLabel>{section.title}</SectionLabel>
                    {chips(section.options)}
                </Stack>
            ))}
            {subjects != null && (
                <Row gap="2" style={styles.wrap}>
                    {subjects.map((subject) => (
                        <SubjectBadge
                            key={subject.id}
                            subject={subject}
                            size="md"
                            showLabel
                            fullLabel
                            selected={selectedIds.includes(String(subject.id))}
                            disabled={disabled}
                            onSelect={() => onToggle(String(subject.id))}
                        />
                    ))}
                </Row>
            )}
            {error != null && error !== "" && (
                <Text size="text-md" color="danger-strong">
                    {error}
                </Text>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    wrap: { flexWrap: "wrap" },
    cards: (sideBySide: boolean) => ({
        flexDirection: sideBySide ? ("row" as const) : ("column" as const),
        gap: theme.spacing["3"],
    }),
    sideCard: { flex: 1, minWidth: 0 },
}))
